import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, redirect, useActionData, useLocation, useNavigate, useNavigation, useOutletContext, useSubmit } from "react-router-dom";
import { toast } from 'react-toastify';
import { addCustomerAddress } from "../../api/customer/address.js";
import i18n from "../../i18n.js";
import { ValidationError } from "../../library/classes/exceptions/index.js";
import Countries from "../../library/dynamics/countries.json";
import { getFieldErrors, getFieldStatus } from "../../library/helper/validation-helper.jsx";
import Modal from "../ui/modal.jsx";
import styles from "./add-address-form.module.scss";

const getFieldStatusClass = (name, errors) => {
  const status = getFieldStatus(name, errors)
  if (!status) {
    return ''
  }
  return `field-status-${status}`
}

const AddAddressForm = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { customerCountryId, customerCompany } = useOutletContext() || {}
  const formRef = useRef(null)
  const submit = useSubmit()
  const navigation = useNavigation()
  const location = useLocation()
  const { errors } = useActionData() || {}
  const isLoading = navigation.formAction === location.pathname
  // Force modal visible flag
  const [isVisible, setIsVisible] = useState(true);

  const handleClose = () => {
    setIsVisible(false);
    // Delay navigation to allow unmount
    setTimeout(() => {
      navigate('..'); // Go back to parent route explicitly
    }, 100); // Short delay to allow Modal to unmount smoothly
  };

  // Effect to re-show modal if opened again
  useEffect(() => {
    setIsVisible(true);
  }, [location.key]);

  return (
    isVisible && (
      <Modal
        title={t('Add New Address')}
        isVisible={true}
        onClose={handleClose}
        oKButtonText={!isLoading ? t('Add New Address') : false}
        onOk={() => {
          submit(formRef.current, {
            method: 'post'
          });
        }}
      >
        <Form ref={formRef} className={`${styles.wrapper}${isLoading ? ' loading' : ''}`} method="post" id="addAddressForm">
          <div className={styles.formItem}>
            <label htmlFor="company">{t('Company')}</label>
            <input name="company" type="text" id="company" defaultValue={customerCompany ?? ""} placeholder={t('Company name')} className={getFieldStatusClass('company', errors)} />
            {getFieldErrors('company', errors)}
          </div>
          <div className={styles.formItem}>
            <label htmlFor="street">{t('Street')} *</label>
            <input name="street" type="text" id="street" placeholder={t('Street name')} className={getFieldStatusClass('street', errors)} />
            {getFieldErrors('street', errors)}
          </div>
          <div className={styles.formItem}>
            <label htmlFor="street_number">{t('Street Number')} *</label>
            <input name="street_number" type="text" id="street_number" placeholder={t('Street Number')} className={getFieldStatusClass('street_number', errors)} />
            {getFieldErrors('street_number', errors)}
          </div>
          <div className={styles.formItem}>
            <label htmlFor="additional_field_1">{t('Additional Field')}</label>
            <input name="additional_field_1" type="text" id="additional_field_1" placeholder={t('Building')} className={getFieldStatusClass('additional_field_1', errors)} />
            {getFieldErrors('additional_field_1', errors)}
          </div>
          <div className={styles.formItem}>
            <label htmlFor="additional_field_2">{t('Additional Field 2')}</label>
            <input name="additional_field_2" type="text" id="additional_field_2" placeholder={t('Floor')} className={getFieldStatusClass('additional_field_2', errors)} />
            {getFieldErrors('additional_field_2', errors)}
          </div>
          <div className={styles.formItem}>
            <label htmlFor="zipcode">{t('Zipcode')} *</label>
            <input name="zipcode"
              type="text"
              id="zipcode"
              placeholder={t('Postal code')}
              className={getFieldStatusClass('zipcode', errors)}
            />
            {getFieldErrors('zipcode', errors)}
          </div>
          <div className={styles.formItem}>
            <label htmlFor="city">{t('City')}</label>
            <input name="city" id="city" type="text" placeholder={t('City')} className={getFieldStatusClass('city', errors)} />
            {getFieldErrors('city', errors)}
          </div>
          <div className={styles.formItem}>
            <label htmlFor="county">{t('County')}</label>
            <input name="county" id="county" type="text" placeholder={t('County or region')} className={getFieldStatusClass('county', errors)} />
            {getFieldErrors('county', errors)}
          </div>
          <div className={styles.formItem}>
            <label htmlFor="country">{t('Country')} *</label>
            <select name="country_id" id="country" defaultValue={customerCountryId ?? ""} className={getFieldStatusClass('country_id', errors)} >
              <option value="" disabled>{t('Select country')}</option>
              {Countries.map((country) => (
                <option key={country.id} value={country.id}>
                  {country.name}
                </option>
              ))}
            </select>
            {getFieldErrors('country_id', errors)}
          </div>
        </Form>
      </Modal >
    )
  )
};

AddAddressForm.Action = async ({ params, request }) => {
  const { quotationToken } = params;
  const data = Object.fromEntries(await request.formData());
  const { searchParams } = new URL(request.url);
  const addressSelector = searchParams.get('addressSelector');
  try {
    if (!data.street || data.street.length === 0) {
      throw new ValidationError(i18n.t('Street is required'), [{
        path: 'street',
        message: 'Street is required'
      }])
    }
    if (!data.street_number || data.street_number.length === 0) {
      throw new ValidationError(i18n.t('Street Number is required'), [{
        path: 'street_number',
        message: 'Street Number is required'
      }])
    }
    if (!data.country_id) {
      throw new ValidationError(i18n.t('Country is required'), [{
        path: 'country_id',
        message: 'Country is required'
      }])
    }
    if (!data.zipcode || data.zipcode.length === 0) {
      throw new ValidationError(i18n.t('Zipcode is required'), [{
        path: 'zipcode',
        message: 'Zipcode is required'
      }])
    }
    const newAddress = await addCustomerAddress(quotationToken, data);
    const address_id = newAddress?.address_id ?? newAddress?.id ?? newAddress?.data?.address_id ?? newAddress?.data?.id ?? newAddress;
    const redirectParams = new URLSearchParams();
    if (address_id) {
      redirectParams.set('newAddressId', address_id);
    }
    if (addressSelector) {
      redirectParams.set('addressSelector', addressSelector);
    }
    const queryString = redirectParams.toString();
    return redirect(queryString ? `../?${queryString}` : "../");
  } catch (error) {
    toast.error(i18n.t(error?.message))
    if (error instanceof ValidationError) {
      return {
        errors: error.errors
      }
    }
    return false
  }
};

export default AddAddressForm;
